import { useContext, useRef } from "react";
import { Box, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import type { EffectivenessData } from "../../types";

const PORTUGUESE_MONTH_MAPPING: Record<string, string> = {
  "janeiro": "Jan",
  "fevereiro": "Fev",
  "março": "Mar",
  "abril": "Abr",
  "maio": "Mai",
  "junho": "Jun",
  "julho": "Jul",
  "agosto": "Ago",
  "setembro": "Set",
  "outubro": "Out",
  "novembro": "Nov",
  "dezembro": "Dez",
};

export type EffectivenessChartData = {
  monthlyData: Record<string, EffectivenessData>;
  ytdData: EffectivenessData;
};

export type StrategicClusterYtdComposedChartProps = {
  title: string;
  titleValue?: string;
  headerRightLabel?: string;
  data: EffectivenessChartData;
  compact?: boolean;
  variant?: "card" | "embedded";
};

export default function StrategicClusterYtdComposedChart({
  title,
  titleValue,
  headerRightLabel,
  data,
  compact = false,
  variant = "card",
}: StrategicClusterYtdComposedChartProps) {
  const chartRef = useRef(null);
  const { theme, isMobile } = useContext(MainContext);
  const embedded = variant === "embedded";

  const monthEntries: [string, EffectivenessData][] = Object.entries(data.monthlyData);

  const monthOrder = Object.keys(PORTUGUESE_MONTH_MAPPING);
  monthEntries.sort((a, b) => {
    return monthOrder.indexOf(a[0]) - monthOrder.indexOf(b[0]);
  });

  const categories: string[] = [
    ...monthEntries.map(([month]) => PORTUGUESE_MONTH_MAPPING[month] || month),
    "YTD"
  ];

  const barData: number[] = [
    ...monthEntries.map(([, monthData]) => monthData.effectivenessPercentage),
    data.ytdData.effectivenessPercentage
  ];

  const lineData: number[] = [
    ...monthEntries.map(([, monthData]) => monthData.effectivenessTarget),
    data.ytdData.effectivenessTarget
  ];

  const pctForAxis: string[] = [
    ...monthEntries.map(([, monthData]) => `${monthData.effectivenessPercentage.toFixed(0)}%`),
    `${data.ytdData.effectivenessPercentage.toFixed(0)}%`
  ];

  const generatedEnergyData: number[] = [
    ...monthEntries.map(([, monthData]) => monthData.generatedEnergyMWh),
    data.ytdData.generatedEnergyMWh
  ];

  const targetEnergyData: number[] = [
    ...monthEntries.map(([, monthData]) => monthData.targetEnergyMWh),
    data.ytdData.targetEnergyMWh
  ];

  const barLabel = "Eficácia (%)";
  const lineLabel = "Meta (%)";;

  const maxDataValue = Math.max(...barData, ...lineData);
  const yAxisMax = maxDataValue > 120 ? maxDataValue : 120;

  const gridBottom = embedded
    ? (isMobile ? 44 : 40)
    : compact
      ? (isMobile ? 26 : 20)
      : (isMobile ? 36 : 32);

  const option: EChartsOption = {
    backgroundColor: "transparent",
    grid: {
      left: isMobile ? "10%" : "8%",
      right: isMobile ? "12%" : "10%",
      bottom: gridBottom,
      top: embedded ? 48 : compact ? 48 : 56,
      containLabel: false,
    },
    legend: {
      show: false,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        formatter: (value: string, index: number) => {
          const pct = pctForAxis[index] ?? "";
          return `{m|${value}}\n{p|${pct}}`;
        },
        rich: {
          m: {
            color: theme.secondaryTextColor || "#8F969E",
            fontSize: isMobile ? 9 : 10,
            lineHeight: 15,
          },
          p: {
            color: "#80D972",
            fontSize: isMobile ? 9 : 10,
            lineHeight: 15,
            fontWeight: 500,
          },
        },
      },
    },
    yAxis: [
      {
        type: "value",
        name: "%",
        position: "left",
        min: 0,
        max: yAxisMax,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.cardBorderColor || "rgba(255,255,255,0.08)",
            type: "dashed",
          },
        },
        axisLabel: {
          color: theme.secondaryTextColor || "#999",
          fontSize: 10,
          formatter: (value: number) => `${value.toFixed(0)}%`,
        },
      },
    ],
    tooltip: {
      trigger: "axis",
      backgroundColor: theme.backgroundColor || "#1a1a1a",
      borderColor: theme.cardBorderColor || "#444",
      textStyle: {
        color: theme.primaryTextColor || "#fff",
      },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return "";
        const axis = params[0] as { axisValue: string; dataIndex: number };
        const dataIndex = axis.dataIndex;
        const percentage = barData[dataIndex];
        const target = lineData[dataIndex];
        const energyMWh = generatedEnergyData[dataIndex];
        const targetMWh = targetEnergyData[dataIndex];

        return `${axis.axisValue}<br/>` +
          `Eficácia: ${percentage.toFixed(1)}%<br/>` +
          `Meta: ${target.toFixed(1)}%<br/>` +
          `Geração: ${energyMWh.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} MWh<br/>` +
          `Meta Geração: ${targetMWh.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} MWh`;
      },
    },
    series: [
      {
        name: barLabel,
        type: "bar",
        yAxisIndex: 0,
        data: barData,
        z: 1,
        barMaxWidth: compact ? 16 : 22,
        itemStyle: {
          color: "#FB773B",
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: lineLabel,
        type: "line",
        yAxisIndex: 0,
        data: lineData,
        z: 3,
        smooth: true,
        symbol: "circle",
        symbolSize: compact ? 5 : 6,
        lineStyle: { color: "#80D972", width: 2 },
        itemStyle: { color: "#80D972" },
      },
    ],
  };

  const minH = compact ? (isMobile ? 200 : 220) : isMobile ? 260 : 300;
  const headerH = embedded ? 40 : 56;
  const chartPixelH = minH - headerH;

  const outerSx = embedded
    ? {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minHeight: minH,
      backgroundColor: "transparent",
      borderRadius: 0,
      padding: 0,
    }
    : {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minHeight: minH,
      backgroundColor: theme.cardBoxBackgroudColor ?? "#1e293b",
      borderRadius: "12px",
      padding: 2,
    };

  return (
    <Box sx={outerSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: embedded ? 0.5 : 1,
          flexWrap: "wrap",
          gap: 1,
          textAlign: "left",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: embedded ? "15px" : compact ? "14px" : "15px",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            {title}
          </Typography>
          {titleValue ? (
            <Typography sx={{ fontSize: embedded ? "15px" : "16px", fontWeight: 700, color: "#80D972" }}>
              - {titleValue}
            </Typography>
          ) : null}
        </Box>
        {headerRightLabel ? (
          <Typography
            sx={{
              fontSize: embedded ? "26px" : compact ? "16px" : "18px",
              fontWeight: 700,
              color: "#80D972",
              textAlign: "right",
            }}
          >
            {headerRightLabel}
          </Typography>
        ) : null}
      </Box>
      <Box
        ref={chartRef}
        sx={{
          width: "100%",
          height: `${chartPixelH}px`,
          minHeight: `${chartPixelH}px`,
        }}
      >
        <RopChart option={option} />
      </Box>
    </Box>
  );
}
